<template>
  <br />
  <div class="col-sm-12">
    <!-- Tabla de presentación -->  
    <div class="card">

      <div class="card-header card-header-compact">
        <h3 class="card-header-title">{{ tableTitle }}</h3>
        <div class="card-header-toolbar">
          <small v-if="loading">Cargando datos...</small>
          <input
            v-model="searchQuery"
            @keyup.enter="onSearch"
            class="form-control form-control-sm"
            placeholder="Buscar por RFC o nombre..."
            style="width: 320px"
          />
          <button type="button" class="btn btn-sm btn-primary" :disabled="loading" @click="onSearch">Buscar</button>
        </div>
      </div>

      <div class="card-body">
        <DataTable
          :rows="tableData"
          :columns="displayColumns"
          :loading="loading"
          :error="error"
          :show-account-button="false"
          show-polizas-button
          @polizas="onPolizas"
          @view="onView"
          @edit="onEdit"
          @delete="onDelete"
        />
        <div v-if="error" class="mt-2">
          <button type="button" class="btn btn-sm btn-outline-primary" @click="retry">Reintentar</button>
        </div>
        <p v-if="!loading && !error && tableData.length === 0" class="text-muted mb-0">{{ emptyMessage }}</p>
      </div>
      <div class="card-footer">
        <div class="d-flex justify-content-between align-items-center mt-2">
          <div>
            <button class="btn btn-sm btn-secondary me-2" :disabled="!hasPrevious || loading"
              @click="prevPage">Anterior</button>
              <button class="btn btn-sm btn-secondary" :disabled="!hasNext || loading"
              @click="nextPage">Siguiente</button>
          </div>
          <div>
            <small>Total de registros: {{ formatNumber(totalItems) }}</small>
            <small v-if="!loading && tableData.length > 0"> Mostrando {{ tableData.length }} de {{
              formatNumber(totalItems)
              }}</small>
          </div>
          <div>
            <small>Página {{ currentPage }} de {{ formatNumber(totalPages) }}</small>
          </div>
        </div>
      </div>

    </div>
    
  </div>

</template>

<script>
import { useBootsrapStore } from '@/stores/bootsrap';
import api from '@/axios';
import DataTable from '@/components/DataTablePolizas.vue';
import { useSeguroStore } from '@/stores/seguroStore';

const STALE = Symbol('stale');

export default {
  setup() {
        const store = useSeguroStore();
        return { store };
  },
  name: 'catalogo',
  components: { DataTable },
  props: {
    tableName: {
      type: String,
      default: 'contratantes',
    },
    tableTitle: {
      type: String,
      default: 'Contratantes',
    },
    labels: {
      type: Object,
      default: () => ({'Id': '#', 'NombreCompleto': 'Contratante', 'rfc': 'RFC', "genero": "Género"}),
    },
    showColumns: {
      type: Array,
      default: () => ['Id', 'NombreCompleto', 'rfc', "genero"],
    },
  },  
  data() {
    return {
      tableColumns: [],
      tableData: [],
      loading: false,
      error: null,
      searchQuery: '',
      // Texto de la última búsqueda confirmada: es el que se envía al paginar,
      // aunque el usuario haya editado la caja sin confirmar.
      appliedSearch: '',
      requestId: 0,
      abortController: null,
      lastPage: 1,
      pageSize: 20,
      currentPage: 1,
      totalItems: 0,
      totalPages: 1,
      hasPrevious: false,
      hasNext: false,
    };
  },

  computed: {
    emptyMessage() {
      return this.appliedSearch
        ? 'No se encontraron contratantes para la búsqueda'
        : 'No hay registros';
    },

    bootstraplist() {
      return useBootsrapStore().bootstrap;
    },

    displayColumns() {
      // Use tableColumns (which contains {text,value}) but ensure text is friendly
      const hidden = ['isactive', 'genero'];
      return this.tableColumns
        .filter(c => !hidden.includes(String(c.value).toLowerCase()))
        .map(c => ({ text: this.friendlyLabel(c.value), value: c.value }));
    },
  },

  created() {
    // merge any external labels passed via prop
    if (this.labels && Object.keys(this.labels).length) {
      this.columnLabels = { ...this.columnLabels, ...this.labels };
    }
    // Búsqueda y página desde la URL (?search=&page=): al volver con "Atrás"
    // desde Pólizas Emitidas se recupera el contexto del listado.
    const query = this.$route?.query || {};
    this.searchQuery = this.appliedSearch = this.normalizeSearch(query.search);
    const page = parseInt(Array.isArray(query.page) ? query.page[0] : query.page, 10);
    this.loadData(page > 1 ? page : 1);
  },

  methods: {
    // Devuelve el payload, null si hubo error, o STALE si otra petición más
    // reciente reemplazó a ésta (su resultado no debe mostrarse).
    async fetchData(page) {
      const id = ++this.requestId;
      if (this.abortController) this.abortController.abort();
      const controller = new AbortController();
      this.abortController = controller;
      try {
        this.loading = true;
        this.error = null;
        const response = await api.get(this.tableName, {
          params: { page, pageSize: this.pageSize, search: this.appliedSearch || undefined },
          signal: controller.signal,
        });
        if (id !== this.requestId) return STALE;
        const payload = response.data;
        if (!payload || !Array.isArray(payload.items)) {
          console.warn('Respuesta inesperada de la API (se esperaba { items, page, pageSize, totalItems, totalPages, hasPrevious, hasNext }):', payload);
          this.error = 'La API devolvió un formato inesperado. Revisá la consola para más detalle.';
          return null;
        }
        return payload;
      } catch (err) {
        if (id !== this.requestId) return STALE;
        console.error('Error fetching data:', err);
        this.error = 'No se pudieron cargar los datos.';
        return null;
      } finally {
        if (id === this.requestId) this.loading = false;
      }
    },

    getColumns(data) {
      if (!Array.isArray(data) || data.length === 0) return [];
      return Object.keys(data[0]);
    },

    buildTableColumns(data) {
      const columns = this.getColumns(data);
      return columns.map(col => ({ text: this.friendlyLabel(col), value: col }));
    },

    buildTableData(items) {
      return items;
    },

    async loadData(page = this.currentPage) {
      this.lastPage = page;
      const result = await this.fetchData(page);
      if (result === STALE) return;
      if (!result) {
        this.tableData = [];
        return;
      }
      this.currentPage = result.page ?? page;
      this.totalItems = result.totalItems ?? 0;
      this.totalPages = result.totalPages ?? 1;
      this.hasPrevious = result.hasPrevious ?? false;
      this.hasNext = result.hasNext ?? false;
      this.tableColumns = this.buildTableColumns(result.items);
      this.tableData = this.buildTableData(result.items);
      this.syncUrl();
    },

    // Refleja búsqueda y página en la URL (replace, sin agregar historial).
    syncUrl() {
      const query = {};
      if (this.appliedSearch) query.search = this.appliedSearch;
      if (this.currentPage > 1) query.page = String(this.currentPage);
      this.$router?.replace({ query });
    },

    normalizeSearch(value) {
      const raw = Array.isArray(value) ? value[0] : value;
      return typeof raw === 'string' ? raw.trim() : '';
    },

    friendlyLabel(key) {
      // Prefer labels prop mappings (raw key or generated base), fallback to generated label
        if (this.labels && Object.prototype.hasOwnProperty.call(this.labels, key)) return this.labels[key];
        const base = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (this.labels && Object.prototype.hasOwnProperty.call(this.labels, base)) return this.labels[base];
        return base;
    },

    isBooleanLike(val) {
      if (typeof val === 'boolean') return true;
      if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        return v === 'true' || v === 'false';
      }
      return false;
    },

    toBoolean(val) {
      if (typeof val === 'boolean') return val;
      if (typeof val === 'string') return val.trim().toLowerCase() === 'true';
      return Boolean(val);
    },

    // Detect ISO-like date strings
    isIsoDateString(val) {
      if (typeof val !== 'string') return false;
      // simple ISO 8601 and common datetime patterns
      const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/;
      const isoRegexNoT = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
      return isoRegex.test(val) || isoRegexNoT.test(val);
    },

    formatDateTime(val) {
      if (val == null) return '';
      let d;
      if (val instanceof Date) {
        d = val;
      } else if (this.isIsoDateString(String(val))) {
        d = new Date(val);
      } else {
        // try parseable
        const parsed = Date.parse(val);
        if (!isNaN(parsed)) d = new Date(parsed);
        else return val;
      }

      if (isNaN(d)) return val;

      const pad = n => String(n).padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = String(d.getFullYear());
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      const seconds = pad(d.getSeconds());
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    },

    formatCell(val, key) {
      // don't change format for Id columns or those ending in 'Id' (case-insensitive)
      if (key && /(^id$|id$)/i.test(key)) {
        return val == null ? '' : String(val);
      }

      // if value is object (like nested), stringify minimally
      if (val && typeof val === 'object') return JSON.stringify(val);
      // format if it's a date-like value
      if (this.isIsoDateString(val) || typeof val === 'number' && !Number.isNaN(val)) {
        // numbers could be timestamps; try to format if reasonable
        const maybeDate = new Date(val);
        if (!isNaN(maybeDate)) return this.formatDateTime(maybeDate);
      }
      return this.formatDateTime(val);
    },

    prevPage() {
      if (this.hasPrevious) this.loadData(this.currentPage - 1);
    },

    nextPage() {
      if (this.hasNext) this.loadData(this.currentPage + 1);
    },

    formatNumber(val) {
      return (Number(val) || 0).toLocaleString('en-US');
    },

    onSearch() {
      this.appliedSearch = this.searchQuery.trim();
      this.loadData(1);
    },

    retry() {
      this.loadData(this.lastPage);
    },

    onNew() {
      this.store.folio = 0;
      // Navegar al detalle de ciudad con id = 0 para crear nuevo registro
      this.$router.push({ name: 'detallePoliza', params: { id: 0 }, query: { tableName: this.tableName } });
    },

    onEdit(item) {
      const id = item.Id || item.id;
      this.$router.push({ name: 'editContratante', params: { id } });
    },

    // Abre Pólizas Emitidas filtrada por el RFC del contratante (navegación normal:
    // "Atrás" regresa a este listado).
    onPolizas(item) {
      const rfc = String(item.rfc ?? item.RFC ?? '').trim();
      if (!rfc) return;
      this.$router.push({ name: 'emitidasPolizas', query: { search: rfc } });
    },

    onView(item) {
      const id = item.Id || item.id;
      this.$router.push({ name: 'detalleContratante', params: { id } });
    },

    onDelete(item) {
      // confirmación simple antes de eliminar (no realiza eliminación real aquí)
      if (confirm(`¿Eliminar el folio '${item.nombre || item.folio}'?`)) {
        // Aquí deberías llamar a la API para eliminar. Por ahora solo registramos en consola.
        try {
          // Llamada a la API para eliminar el registro
          api.delete(`polizas/${item.folio}`);
        } catch (err) {
          console.error('Error deleting item:', err);
          this.error = 'No se pudo eliminar el registro.';
          return;
        }
        this.tableData = this.tableData.filter(i => i.folio !== item.folio);
      }
    },

    onStatus(item) {
      // Alternar el estado IsActive del seguro
      const newStatus = !this.toBoolean(item.IsActive);
      try {
        // Llamada a la API para actualizar el estado
        api.patch(`polizas/${item.folio}`, { IsActive: newStatus });
        // Actualizar localmente
        item.IsActive = newStatus;
      } catch (err) {
        console.error('Error updating status:', err);
        this.error = 'No se pudo actualizar el estado del registro.';
      }
    },

  },
};

</script>

<style scoped>
/* Center feather SVG icons inside small action buttons */
.icon-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.icon-btn svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}
</style>
